/**
 * DuplicateExercisesDialog — coach tool for cleaning up legacy duplicate
 * exercises in the library.
 *
 * Owner spec 2026-02-17: no duplicate exercise names ever. The CREATE
 * and PATCH endpoints already reject case-insensitive collisions; this
 * dialog cleans up pre-existing collisions that snuck in before the
 * rule existed.
 *
 * Each cluster shows all rows sharing a (case-insensitive, trimmed)
 * name, sorted by workout reference count (most-used first). The coach
 * picks which row is the "keeper" (defaults to the most-used row) and
 * the rest are bulk-archived via /coach/exercises/bulk-archive.
 *
 * Soft-archive only — historical workouts that reference the archived
 * IDs still resolve to the exercise name on read.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, AlertTriangle, CheckCircle2, Trash2, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DuplicateExercisesDialog({ onClose, onResolved }) {
    const [loading, setLoading] = useState(true);
    const [clusters, setClusters] = useState([]);
    const [stats, setStats] = useState({ total_clusters: 0, total_excess_rows: 0 });
    const [keepers, setKeepers] = useState({});  // { clusterKey: exercise_id }
    const [busy, setBusy] = useState(false);

    const refresh = async () => {
        setLoading(true);
        try {
            const r = await axios.get(`${API}/coach/exercises/duplicates`, { withCredentials: true });
            setClusters(r.data.clusters || []);
            setStats({
                total_clusters: r.data.total_clusters || 0,
                total_excess_rows: r.data.total_excess_rows || 0,
            });
            // Default keeper = first row in each cluster (already sorted by
            // workout_refs DESC server-side, so default = most-used copy).
            const defaultKeepers = {};
            (r.data.clusters || []).forEach((c) => {
                if (c.rows?.[0]) defaultKeepers[c.key] = c.rows[0].exercise_id;
            });
            setKeepers(defaultKeepers);
        } catch (e) {
            toast.error("Couldn't load duplicates", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []);

    const archiveAllExceptKeepers = async () => {
        const toArchive = [];
        clusters.forEach((c) => {
            const keeperId = keepers[c.key];
            (c.rows || []).forEach((r) => {
                if (r.exercise_id !== keeperId) toArchive.push(r.exercise_id);
            });
        });
        if (toArchive.length === 0) {
            toast.info("Nothing selected for archive.");
            return;
        }
        if (!window.confirm(
            `Archive ${toArchive.length} duplicate exercise${toArchive.length === 1 ? "" : "s"}? `
            + "Their historical workout references will still resolve to the exercise name."
        )) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/exercises/bulk-archive`,
                { exercise_ids: toArchive },
                { withCredentials: true },
            );
            toast.success(`Archived ${r.data.archived} duplicate${r.data.archived === 1 ? "" : "s"}`);
            onResolved?.();
            await refresh();
        } catch (e) {
            toast.error("Archive failed", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setBusy(false);
        }
    };

    const archiveSingleCluster = async (cluster) => {
        const keeperId = keepers[cluster.key];
        const toArchive = (cluster.rows || []).filter((r) => r.exercise_id !== keeperId).map((r) => r.exercise_id);
        if (!toArchive.length) return;
        setBusy(true);
        try {
            const r = await axios.post(
                `${API}/coach/exercises/bulk-archive`,
                { exercise_ids: toArchive },
                { withCredentials: true },
            );
            toast.success(`Archived ${r.data.archived} dup${r.data.archived === 1 ? "" : "s"} of "${cluster.rows[0].exercise_name}"`);
            onResolved?.();
            await refresh();
        } catch (e) {
            toast.error("Archive failed", { description: e?.response?.data?.detail || "Server error" });
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-end md:items-center justify-center p-0 md:p-6 overflow-y-auto">
            <div
                data-testid="duplicate-exercises-dialog"
                className="bg-zinc-950 border border-zinc-800 w-full md:max-w-4xl my-6 max-h-[92vh] flex flex-col rounded-none md:rounded-lg"
            >
                <div className="h-1 bg-brand-red shrink-0" />
                <div className="px-6 py-4 border-b border-zinc-900 flex items-center justify-between shrink-0">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">
                            Library cleanup
                        </div>
                        <h2 className="font-display text-2xl tracking-wider text-white mt-1">
                            DUPLICATE EXERCISES
                        </h2>
                    </div>
                    <button
                        type="button"
                        data-testid="dup-dialog-close"
                        onClick={onClose}
                        className="text-zinc-300 hover:text-white"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {loading ? (
                    <div className="py-20 flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-brand-red animate-spin" />
                    </div>
                ) : clusters.length === 0 ? (
                    <div className="px-6 py-16 text-center" data-testid="dup-empty-state">
                        <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-4" />
                        <div className="font-display text-xl tracking-wider text-white mb-2">
                            LIBRARY IS CLEAN
                        </div>
                        <div className="text-sm text-zinc-300 max-w-md mx-auto">
                            No duplicate exercise names detected. New duplicates are blocked
                            automatically at create + rename — you should never see this
                            screen with results again.
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="px-6 py-4 bg-amber-950/30 border-b border-amber-900/50 flex items-center justify-between gap-4 shrink-0">
                            <div className="flex items-center gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
                                <div>
                                    <div className="text-sm text-amber-200 font-semibold">
                                        {stats.total_clusters} duplicate cluster{stats.total_clusters === 1 ? "" : "s"} · {stats.total_excess_rows} excess row{stats.total_excess_rows === 1 ? "" : "s"} to archive
                                    </div>
                                    <div className="text-[11px] text-amber-300/80 mt-0.5">
                                        Keeper defaults to the most-used copy. Change the radio if you want to keep a different one.
                                    </div>
                                </div>
                            </div>
                            <button
                                type="button"
                                data-testid="dup-archive-all"
                                onClick={archiveAllExceptKeepers}
                                disabled={busy}
                                className="btn-tactical shrink-0 inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Archive {stats.total_excess_rows} non-keeper{stats.total_excess_rows === 1 ? "" : "s"}
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
                            {clusters.map((c) => (
                                <div key={c.key} className="border border-zinc-800 bg-zinc-900/40 rounded" data-testid={`dup-cluster-${c.key}`}>
                                    <div className="px-4 py-2 border-b border-zinc-800 bg-zinc-900/60 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-mono">
                                                &quot;{c.key}&quot;
                                            </span>
                                            <span className="text-[10px] tracking-[0.18em] uppercase text-brand-red">
                                                {c.count} copies
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            data-testid={`dup-cluster-archive-${c.key}`}
                                            onClick={() => archiveSingleCluster(c)}
                                            disabled={busy}
                                            className="text-[10px] tracking-[0.18em] uppercase text-zinc-300 hover:text-brand-red disabled:opacity-40 inline-flex items-center gap-1.5"
                                        >
                                            <ArrowRight className="w-3 h-3" /> Clean this cluster
                                        </button>
                                    </div>
                                    <table className="w-full text-xs">
                                        <thead className="text-[10px] tracking-[0.18em] uppercase text-zinc-400">
                                            <tr className="border-b border-zinc-900">
                                                <th className="px-3 py-2 text-left w-16">Keep</th>
                                                <th className="px-3 py-2 text-left">Name</th>
                                                <th className="px-3 py-2 text-left">Category</th>
                                                <th className="px-3 py-2 text-right">Workout refs</th>
                                                <th className="px-3 py-2 text-right">Created</th>
                                                <th className="px-3 py-2 text-right w-10" />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(c.rows || []).map((row) => {
                                                const isKeeper = keepers[c.key] === row.exercise_id;
                                                return (
                                                    <tr
                                                        key={row.exercise_id}
                                                        data-testid={`dup-row-${row.exercise_id}`}
                                                        className={`border-b border-zinc-900 last:border-b-0 ${isKeeper ? "bg-emerald-950/20" : ""}`}
                                                    >
                                                        <td className="px-3 py-2.5">
                                                            <input
                                                                type="radio"
                                                                name={`keeper-${c.key}`}
                                                                data-testid={`dup-keep-${row.exercise_id}`}
                                                                checked={isKeeper}
                                                                onChange={() => setKeepers((k) => ({ ...k, [c.key]: row.exercise_id }))}
                                                                className="accent-brand-red w-4 h-4"
                                                            />
                                                        </td>
                                                        <td className="px-3 py-2.5 text-white truncate max-w-md">
                                                            {row.exercise_name}
                                                            {isKeeper && (
                                                                <span className="text-[9px] tracking-[0.2em] uppercase text-emerald-400 ml-2">
                                                                    Keeper
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-zinc-300">
                                                            {row.category || "—"}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right font-mono text-zinc-200">
                                                            {row.workout_refs ?? 0}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right text-zinc-400 text-[10px]">
                                                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : "—"}
                                                        </td>
                                                        <td className="px-3 py-2.5 text-right text-[9px] text-zinc-500 font-mono">
                                                            {row.exercise_id.slice(-6)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
